import type { ChatSliceState, GenerationStatus } from "../types/redux";

export interface UpdateMessagePayload {
  clientMessageId?: string;
  text?: string;
  updates?: Record<string, unknown>;
}

export interface StageTurnResultsPayload {
  turnId: string;
  results?: unknown[];
  documents?: unknown[];
}

export interface SetTurnTodoPlanPayload {
  turnId: string;
  plan?: unknown;
  scopeSummary?: unknown;
}

export interface ClearTurnTodoPlanPayload {
  turnId: string;
}

export interface AddGenerationProgressPayload {
  projectId: string;
}

export interface UpdateGenerationStatusPayload {
  projectId: string;
  status: GenerationStatus;
  error?: string;
}

/** Chunk accumulator for reassembling chunked results payloads. */
export interface ChunkAccumulator {
  chunks: Array<{
    chunkIndex: number;
    payload: {
      results: unknown[];
      documents: unknown[];
      researchDocs: unknown[];
      facts: unknown[];
    };
    sizeBytes: number;
  }>;
  expected: number;
  receivedBytes: number;
  turnId: string;
  projectId: string;
}

/** Redux dispatch as the chat thunks see it — thunks and plain actions alike. */
export type ChatDispatch = (action: any) => any;

/** What an activity handler needs from the store to do its work. */
export interface ActivityContext {
  dispatch: ChatDispatch;
  getState: () => any;
}

export const WELCOME_MESSAGE_TEXT =
  "Welcome! I'm here to help you with deal analysis and research. Please choose one of the options below to get started.";

export const initialState: ChatSliceState = {
  messages: [
    {
      sender: "chatbot",
      text: WELCOME_MESSAGE_TEXT,
    },
  ],
  isBotTyping: false,
  mcpEnvironment: "primary", // 'primary' or 'secondary'
  dataVersion: 2, // 1 for 'Synthetic', 2 for 'Public'
  conversationId: null, // DirectLine conversation ID for debugging and testing
  bootstrapInitialized: false, // Track if bootstrap has been attempted
  bootstrapError: null, // Track bootstrap errors
  pendingStreamingMessages: {}, // Map of turnId -> { text, sequence, messageIndex }
  currentWorkflowStatus: null, // Current workflow status for action "thinking" indicators
  chatInputReady: true, // Flag to control whether user can submit new messages
  useCaseSelected: false, // Track if user has selected a use case to unlock chat
  selectedUseCase: null as string | null, // 'past-deals', 'offer-letter', etc.
  pendingTurnResults: {}, // Phase 3: Map of turnId -> { results, documents } staged for finalization
  todoPlanByTurn: {}, // Map of turnId -> { plan: TurnTodoPlan, scopeSummary?: object }
  chatHistoryLoading: false, // Track if we're loading conversation history
  chatHistoryError: null, // Track errors when loading conversation history
};
